import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2 } from 'lucide-react';
import { useAuthStore } from '@/lib/authStore';
import { extractError } from '@/lib/apiClient';

const schema = z.object({
  email: z.string().email(),
  password: z.string().min(8),
});

type Values = z.infer<typeof schema>;

export function LoginPage() {
  const login = useAuthStore((s) => s.login);
  const isLoading = useAuthStore((s) => s.isLoading);
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  const { register, handleSubmit, formState: { errors } } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '' },
  });

  async function onSubmit(values: Values) {
    setError(null);
    try {
      await login(values.email.trim().toLowerCase(), values.password);
      navigate({ to: '/' });
    } catch (e) {
      setError(extractError(e));
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6">
      <div className="w-full max-w-md card">
        <div className="mb-8 text-center">
          <div className="mb-2 text-4xl">🌙</div>
          <h1 className="text-2xl font-bold">Nightlife Venue Dashboard</h1>
          <p className="mt-2 text-sm text-text-secondary">
            Manage your boosts, view analytics, and grow your nightlife brand.
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label htmlFor="email" className="label mb-1 block">Email</label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              className="input"
              placeholder="owner@yourvenue.com"
              {...register('email')}
            />
            {errors.email ? (
              <p className="mt-1 text-sm text-danger">{errors.email.message}</p>
            ) : null}
          </div>

          <div>
            <label htmlFor="password" className="label mb-1 block">Password</label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              className="input"
              placeholder="••••••••"
              {...register('password')}
            />
            {errors.password ? (
              <p className="mt-1 text-sm text-danger">{errors.password.message}</p>
            ) : null}
          </div>

          {error ? (
            <div className="rounded-xl border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger">
              {error}
            </div>
          ) : null}

          <button type="submit" disabled={isLoading} className="btn-primary w-full">
            {isLoading ? <Loader2 size={16} className="animate-spin" /> : null}
            Sign in
          </button>
        </form>

        <p className="mt-6 text-center text-xs text-text-muted">
          Trouble signing in? Use the mobile app to recover your account.
        </p>
      </div>
    </div>
  );
}
